import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {IntlProvider} from 'react-intl';


import config from '../../../../config';

import * as i18nConstants from '../i18nConstants';

import {useIsAuthenticated} from '../../Auth';
import {useUserLocaleTag} from '../../User';

import LoadingIndicator from '../../../global/LoadingIndicator';


const generateI18nLoaderKey = (localeTag, isAuthenticated) => {

    return `${localeTag}${isAuthenticated ? '-p' : ''}`;
};


const generateI18nLoader = (localeTag, isAuthenticated) => {

    const key = generateI18nLoaderKey(localeTag, isAuthenticated);

    return {
        key,
        loader: () => import(`../../../../data/lang/${key}.json`)
    };
};


// needs to be pre-defined to statically analyze the available imports
const asyncMessageLoader = {};
config.locale.available.forEach(({tag}) => {

    const publicI18nLoader = generateI18nLoader(tag, false);
    asyncMessageLoader[publicI18nLoader.key] = publicI18nLoader.loader;

    const privateI18nLoader = generateI18nLoader(tag, true);
    asyncMessageLoader[privateI18nLoader.key] = privateI18nLoader.loader;
});


function I18nProvider(props) {

    let [{isLoaded, localeTag, messages}, setLocale] = useState({
        isLoaded: false,
        localeTag: props.localeTag,
        messages: {},
        ...props.providerProps
    });

    const isAuthenticated = useIsAuthenticated();

    // switch/load user-locale language when available/on-change
    const userLocaleTag = useUserLocaleTag();
    if (userLocaleTag && userLocaleTag !== localeTag) {
        localeTag = userLocaleTag;
    }
    const currentKey = generateI18nLoaderKey(localeTag, isAuthenticated);

    useEffect(() => {

        // always load the public one
        const i18nLoaders = [
            asyncMessageLoader[generateI18nLoaderKey(localeTag, false)]()
        ];
        if (isAuthenticated) {
            i18nLoaders.push(asyncMessageLoader[generateI18nLoaderKey(localeTag, true)]());
        }

        (async () => {
            const loadedMessages = (await Promise.all(i18nLoaders))
                .reduce((previousMessages, currentMessages) => {

                    return {
                        ...previousMessages,
                        ...currentMessages.default
                    };
                }, {});

            // resolve references within messages
            Object.entries(loadedMessages)
                .forEach(([key, message]) => {

                    const matches = message.match(/^\${([a-z\d.]+)}$/i);
                    if (matches) {
                        const referencedKey = matches[1];
                        loadedMessages[key] = loadedMessages[referencedKey];
                    }
                });

            setLocale({
                isLoaded: true,
                localeTag,
                messages: loadedMessages
            });
        })();

    }, [currentKey]);

    if (!isLoaded) {

        return <LoadingIndicator/>;
    }

    return (
        <IntlProvider
            defaultLocale={props.defaultLocaleTag}
            locale={localeTag}
            messages={messages}
        >
            {props.children}
        </IntlProvider>
    );
}


I18nProvider.propTypes = {
    children: PropTypes.any,
    defaultLocaleTag: PropTypes.oneOf(i18nConstants.PLATFORM_LOCALE_TAGS),
    localeTag: PropTypes.oneOf(i18nConstants.PLATFORM_LOCALE_TAGS),
    providerProps: PropTypes.shape({
        isLoaded: PropTypes.bool,
        localeTag: PropTypes.string,
        messages: PropTypes.object
    })
};


I18nProvider.defaultLocales = {
    defaultLocaleTag: i18nConstants.PLATFORM_LOCALE_TAG_DEFAULT
};


export default I18nProvider;
